/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {startTransition, useState, useMemo, useEffect, useRef} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'

// Chakra Components
import {
    Box,
    Button,
    Stack,
    Grid,
    GridItem,
    Container,
    Skeleton,
    useDisclosure,
    Heading,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import BirthdayPromoCard from './partials/birthday-promo-card'
import CartCta from './partials/cart-cta'
import CartTermsAndConditions from './partials/cart-terms-and-conditions'
import CartSecondaryButtonGroup from './partials/cart-secondary-button-group'
import CartSkeleton from './partials/cart-skeleton'
import CartTitle from './partials/cart-title'
import ConfirmationModal from '@salesforce/retail-react-app/app/components/confirmation-modal'
import EmptyCart from './partials/empty-cart'
import OrderSummary from '../../components/order-summary'
import PickupOrDelivery from '@salesforce/retail-react-app/app/components/pickup-or-delivery'
import ProductItemList from '../../components/product-item-list'
import ProductViewModal from '../../components/product-view-modal'
import CartRecommendation from './partials/cart-recommendation'
import {DELIVERY_OPTIONS} from '@salesforce/retail-react-app/app/components/pickup-or-delivery'
import GiftCertificateItems from '../../components/gift-certificate-items'
import {useStoreContext} from '../../contexts/store'
import MAOService from '../../services/mao'
import CartPurpledotBanner from './partials/cart-purpledot-banner'
import '@adyen/adyen-salesforce-pwa/dist/app/adyen.css'
import GiftWrapBox from '../../components/gift-wrap/index.jsx'

// Hooks
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list'
import useMonetate from '../../hooks/use-monetate'
import {useStoreLocatorModal} from '@salesforce/retail-react-app/app/hooks/use-store-locator'
import useCustomerGroups from '../../hooks/use-customer-groups'

// Bonus Product Utilities
import {
    useBasketProductsWithPromotions,
    findAllBonusProductItemsToRemove,
    getBonusProductsForSpecificCartItem
} from '@salesforce/retail-react-app/app/utils/bonus-product'
import {useBonusProductViewModal} from '@salesforce/retail-react-app/app/hooks/use-bonus-product-view-modal'
import BonusProductViewModal from '@salesforce/retail-react-app/app/components/bonus-product-view-modal'

// Constants
import {
    API_ERROR_MESSAGE,
    TOAST_ACTION_VIEW_WISHLIST,
    TOAST_MESSAGE_ALREADY_IN_WISHLIST,
    STORE_LOCATOR_IS_ENABLED
} from '@salesforce/retail-react-app/app/constants'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG} from '@salesforce/retail-react-app/app/pages/cart/partials/cart-secondary-button-group'

// Utilities
import debounce from 'lodash/debounce'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {
    useShopperBasketsMutation,
    useProducts,
    useShopperCustomersMutation,
    useStores,
    usePromotions,
    useAccessToken,
    useCommerceApi,
    useCustomerId,
    useCustomerOrders
} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {isPickupShipment} from '@salesforce/retail-react-app/app/utils/shipment-utils'
import {useSelectedStore} from '@salesforce/retail-react-app/app/hooks/use-selected-store'
import {useMultiship} from '@salesforce/retail-react-app/app/hooks/use-multiship'
import useBirthdayCoupons from '../../hooks/use-birthday-coupons'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import BMSMMeter from '../../components/promo-callout/bmsm'
import FreeShippingProgressBar, {
    FreeShippingProgressSkeleton
} from './partials/free-shipping-progress-bar'
import GwpPwpCartCallout from './partials/gwp-pwp-callout'
import CartDeliveryOptions from './partials/cart-delivery-options'
import LoadingSpinner from '../../components/loading-spinner/index.jsx'
import CartEndorsementCallout from './partials/cart-endorsement-callout'
import AcceptedPaymentMethods from './partials/accepted-payment-methods'

import WishlistInCart from './partials/wishlist-in-cart'
import PwpCarousel from './partials/pwp-carousel'
import {
    getCharitableDonationItem,
    getGlobaleRestrictedList,
    getOutOfStockProductsList,
    isAnyCbProductOutOfStock,
    isEGiftCardInBasket,
    isFineJewelryExists
} from '../../utils/basket-utils'
import {useGlobaleContext, useResolvedCountryISO} from '../../contexts/globale'
import useContentAsset from '../../hooks/use-content-asset.js'
import CustomApi from '../../hooks/use-custom-api.js'
import {useQueryClient} from '@tanstack/react-query'
import useTagManager from '../../hooks/use-tag-manager.js'
import BoltWrapper from '../checkout/partials/bolt.jsx'
import {CheckoutProvider} from '../checkout/util/checkout-context.js'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage/cookie.js'
import {onClient} from '@salesforce/commerce-sdk-react/utils.js'
import {Helmet} from 'react-helmet'
import {CART_TITLE, TWO_DAY_METHOD_ID} from '../../constants.js'
import {fetchShippingMethods} from '../../adyen/lib/contexts/adyen-express-checkout-context.jsx'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site.js'
import {scheduleAfterPaint} from '../../utils/common-utils.js'
import {useSearchParams} from '../../hooks/index.js'

const DEBOUNCE_WAIT = 750
const stickyExpandedSummaryStyles = {
    '&:has(> .mobile-summary .summary-button[aria-expanded=true]) > .shipping-banner': {
        display: 'none'
    }
}

const Cart = () => {
    const cartStyles = useMultiStyleConfig('CartStyles')
    const customapi = new CustomApi()
    const queryClient = useQueryClient()
    const {site} = useMultiSite()
    const tagManager = useTagManager()
    const api = useCommerceApi()
    const {proxy} = api.shopperExperience.clientConfig
    const {getTokenWhenReady} = useAccessToken()
    const {
        data: basket,
        isLoading,
        derivedData,
        isError: isBasketError,
        error: basketError
    } = useCurrentBasket()

    const {countryISO, currencyCode, setRefreshPrices, globaleCurrency} = useGlobaleContext()
    const resolvedCountryISO = useResolvedCountryISO()
    const [showPriceSkeleton, setShowPriceSkeleton] = useState(true)
    const [showOrceDownWarning, setShowOrceDownWarning] = useState(false)
    const [currencySymbol, setCurrencySymbol] = useState('$')
    const [globaleUnavailableSKUs, setGlobaleUnavailableSKUs] = useState([])
    const [isMobileOrderSummaryExpanded, setIsMobileOrderSummaryExpanded] = useState(true)
    const updateShippingMethod = useShopperBasketsMutation('updateShippingMethodForShipment')
    const [selectedDelivery, setDelivery] = useState(
        basket?.shipments?.[0]?.shippingMethod?.c_storePickupEnabled ? 'storepickup' : 'delivery'
    )
    const [isBopisLoading, setisBopisLoading] = useState(false)
    const [isGlobaleInventoryLoading, setIsGlobaleInventoryLoading] = useState(false)
    const [hasBopisError, setBopisError] = useState(false)

    const multishipEnabled = getConfig()?.app?.multishipEnabled ?? true
    const storeLocatorEnabled = getConfig()?.app?.storeLocatorEnabled ?? STORE_LOCATOR_IS_ENABLED

    // State for tracking items being removed (for UI feedback)
    const [removingItemIds, setRemovingItemIds] = React.useState([])

    // ChangeUp config
    const {
        app: {commerceAPI}
    } = getConfig()
    const {changeUp, purpleDot} = commerceAPI
    const {configChangeup} = changeUp || {}
    const [oosBopisSkus, setOosBopisSkus] = useState([])
    const {config: pdConfig} = purpleDot || {}
    let changeUpProductID = null
    let enablePurpleDot = false

    try {
        if (configChangeup) {
            const jsonConfig = JSON.parse(configChangeup)
            changeUpProductID = jsonConfig.enabled ? jsonConfig.productID : null
        }
        if (pdConfig) {
            const jsonPurpleDotConfig = JSON.parse(pdConfig)
            enablePurpleDot = jsonPurpleDotConfig.enabled
        }
    } catch (e) {
        console.error(e.message)
    }

    // Pickup in Store - inventory at current store and all unique store IDs from all shipments
    const {selectedStore} = useSelectedStore()
    const selectedInventoryId = selectedStore?.inventoryId || null
    const allStoreIds = derivedData?.pickupStoreIds?.join(',') ?? ''
    const {data: storeData} = useStores(
        {
            parameters: {
                ids: allStoreIds
            }
        },
        {
            enabled: !!allStoreIds && storeLocatorEnabled
        }
    )
    const uniqueInventoryIds = [
        ...new Set(
            [selectedInventoryId]
                .concat(storeData?.data?.map((store) => store.inventoryId))
                .filter(Boolean)
        )
    ].join(',')

    const {updateDeliveryOption, updateShipmentsWithoutMethods} = useMultiship(basket)
    const productIds =
        [
            ...(basket?.productItems?.map(({productId}) => productId).filter(Boolean) || []),
            ...(basket?.giftCertificateItems?.map(({c_productID}) => c_productID).filter(Boolean) ||
                [])
        ].join(',') || ''

    // Bonus Product Logic
    const {data: productsWithPromotions, ruleBasedQualifyingProductsMap} =
        useBasketProductsWithPromotions(basket)
    const bonusProductViewModal = useBonusProductViewModal()

    const {
        data: products,
        isLoading: isProductsLoading,
        isError: isProductsError,
        error: productsError
    } = useProducts(
        {
            parameters: {
                ids: productIds,
                allImages: true,
                perPricebook: true,
                c_selectedCountry: countryISO,
                c_selectedCurrency: currencyCode,
                expand: 'availability,promotions,options,images,prices,variations',
                ...(uniqueInventoryIds ? {inventoryIds: uniqueInventoryIds} : {})
            }
        },
        {
            enabled: Boolean(productIds) && !!countryISO && !!currencyCode,
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )
    const {store} = useStoreContext()
    // Fire GA4 view_cart whenever cart contents change (add / remove / quantity change).
    const cartItemsHash = useRef(null)

    // Mirrors the line items tagManager.viewCart reports on, so the change signature
    // below tracks exactly what ends up in the event payload.
    const analyticsLineItems = useMemo(
        () => [
            ...(basket?.productItems || []).filter((p) => !p?.c_hiddenFluidProduct),
            ...(basket?.giftCertificateItems || [])
        ],
        [basket]
    )

    const {data: customer} = useCurrentCustomer()
    const {customerId, isRegistered} = customer || {}
    const quantityTotal = basket?.productItems?.reduce((s, i) => s + i?.quantity, 0)
    const {data: customerGroupIds} = useCustomerGroups()
    const [maoBOPISProductsAvailability, setMAOBOPISProductsAvailability] = useState([])

    const isCustomerEmployee =
        customer?.c_isEmployeeCustomer ||
        customerGroupIds?.customerGroupIds?.indexOf('Employee') !== -1
    const getBOPISAvailability = async () => {
        const selectedShippingOption = basket?.shipments?.[0]?.shippingMethod?.c_storePickupEnabled
        if (!selectedShippingOption) {
            return
        }
        setShowPriceSkeleton(false) // 'getBOPISAvailability' will never run for Globale customers
        if (!store) {
            return
        }

        let productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? ''
        let productIdsArray = productIds.length > 0 ? productIds?.split(',') : []

        setOosBopisSkus([])
        setBopisError(false)

        if (productIdsArray.length) {
            productIdsArray = productIdsArray
                ?.filter((id) => id !== 'colorbar' && id !== changeUpProductID)
                ?.map((id) => id?.substring(1, id.length - 1))

            const token = await getTokenWhenReady()
            const mao = new MAOService(basket, `Bearer ${token}`)
            const maoBOPISProductsAvailability = await mao.getProductAvailabilityForStore(store)
            const itemsInMAOResponse = maoBOPISProductsAvailability?.map((prod) => {
                return prod.productId
            })

            setMAOBOPISProductsAvailability(maoBOPISProductsAvailability)

            let oosBOPISItems = maoBOPISProductsAvailability
                ?.filter((prod) => prod.quantity === 0)
                .map((item) => {
                    return item.productId
                })

            let colorBarProducts = basket?.productItems
                ?.filter((prod) => prod.productId == 'colorbar')
                .map((item) => {
                    return item.productId
                })

            // colorbar products are not available for bopis. Add oos by default
            if (colorBarProducts?.length) {
                oosBOPISItems.push(colorBarProducts)
            }

            // add as oos also products not present in MAO response
            productIdsArray.forEach((pid) => {
                if (itemsInMAOResponse.indexOf(pid) === -1) {
                    oosBOPISItems.push(pid)
                }
            })

            if (oosBOPISItems?.length) {
                setOosBopisSkus(oosBOPISItems)
                setBopisError(true)
            }
        }
        setisBopisLoading(false)
    }

    const getGlobalePrices = async (showSkeleton = true) => {
        if (!currencyCode || !countryISO) {
            return
        }
        try {
            if (showSkeleton) {
                setShowPriceSkeleton(true)
            }
            const token = await getTokenWhenReady()
            const parameters = {
                c_currencyCode: currencyCode,
                c_countryISO: countryISO
            }
            const {currencySymbol} = await customapi.post(
                token,
                'globale-prices',
                '/',
                parameters,
                proxy
            )
            setCurrencySymbol(currencySymbol)
            await queryClient.invalidateQueries({
                predicate: (query) =>
                    query.queryKey.some(
                        (segment) => typeof segment === 'string' && segment.includes('/baskets')
                    )
            })
        } catch (e) {
            if (showSkeleton) {
                setShowPriceSkeleton(false)
            }
            console.warn("Error occurred in 'globale-prices' on cart page", e.message)
        }
        setRefreshPrices(false)
        if (showSkeleton) {
            setShowPriceSkeleton(false)
        }
    }

    const checkDcInventory = async () => {
        try {
            const token = await getTokenWhenReady()
            const parameters = {
                c_currencyCode: currencyCode,
                c_countryISO: countryISO
            }
            const response = await customapi.post(
                token,
                'dc-inventory-validation',
                '/',
                parameters,
                proxy
            )
            if (response?.status) {
                getGlobalePrices(true)
                setGlobaleUnavailableSKUs(response?.availabilityResponse?.unavailableSKUs || [])
            }
            setIsGlobaleInventoryLoading(false)
        } catch (e) {
            setGlobaleUnavailableSKUs([])
            setIsGlobaleInventoryLoading(false)
            console.error('Error occurred in checkDcInventory, ', e.message)
        }
    }

    useEffect(() => {
        const selectedShippingOption = basket?.shipments?.[0]?.shippingMethod?.c_storePickupEnabled
            ? 'storepickup'
            : 'delivery'

        if (countryISO && countryISO !== 'US' && currencyCode) {
            setIsGlobaleInventoryLoading(true)
            window.setTimeout(() => {
                checkDcInventory()
            }, 900)
        }
        if (countryISO === 'US') {
            setShowPriceSkeleton(false)
            setGlobaleUnavailableSKUs([])
        }
        if (selectedShippingOption === 'storepickup') {
            setisBopisLoading(true)
            getBOPISAvailability()
        }
    }, [countryISO, quantityTotal, currencyCode])

    const clearBopisFlag = async () => {
        basket?.productItems?.forEach((productItem) => {
            productItem.c_oosAtStore = false
        })
        setOosBopisSkus([])
        setBopisError(false)
    }

    // trigger view_cart GTM event
    useEffect(() => {
        if (
            isProductsLoading ||
            !products ||
            !analyticsLineItems.length ||
            isBopisLoading ||
            isGlobaleInventoryLoading
        ) {
            return
        }

        const selectedShippingOption = basket?.shipments?.[0]?.shippingMethod?.c_storePickupEnabled
            ? 'storepickup'
            : 'delivery'

        // Gift certificate lines carry no quantity, so amount stands in for their edits.
        const sig = analyticsLineItems
            .map((i) => `${i.productId || i.c_productID}:${i.quantity ?? 1}:${i.amount}`)
            .join('|')
            .concat(selectedShippingOption)

        if (sig === cartItemsHash.current) {
            return
        }

        // Bail before recording the signature: an unresolved line means the payload would
        // carry undefined ids, and we still want the event once the details arrive.
        const allResolved = analyticsLineItems.every((i) => products[i.productId || i.c_productID])

        if (!allResolved) {
            return
        }

        cartItemsHash.current = sig

        let oosSKUs = []
        if (selectedShippingOption === 'storepickup' || countryISO !== 'US') {
            oosSKUs = globaleUnavailableSKUs.concat(oosBopisSkus)
        }

        tagManager.viewCart(basket, products, basket?.couponItems?.[0]?.code, oosSKUs)
    }, [
        basket,
        products,
        isProductsLoading,
        analyticsLineItems,
        isBopisLoading,
        isGlobaleInventoryLoading
    ])

    const updateItemsInBasket = async (body) => {
        const token = await getTokenWhenReady()
        await api.shopperBaskets.updateItemsInBasket({
            parameters: {basketId: basket.basketId},
            headers: {Authorization: `Bearer ${token}`},
            body: body
        })
    }

    const clearStoreInLineItems = () => {
        const productItemsToUpdate = []

        basket?.productItems?.forEach((pli) => {
            if (pli?.productId !== changeUpProductID) {
                productItemsToUpdate.push({
                    itemId: pli.itemId, // required field
                    quantity: pli.quantity, // required field
                    c_fromStoreId: '',
                    c_storeInventory: 0
                })
            }
        })

        if (productItemsToUpdate.length) {
            updateItemsInBasket(productItemsToUpdate) // Not used in the App, so fire and forget
        }
    }

    const setStoreInLineItems = () => {
        const productItemsToUpdate = []

        basket?.productItems?.forEach((pli) => {
            if (pli?.productId !== changeUpProductID) {
                const product = productsByItemId?.[pli.itemId]
                const manufacturerSKU = pli.c_sku || pli.productId
                const storeInventoryStock =
                    product?.inventories?.find((inventory) => inventory.id === store.inventoryId)
                        ?.stockLevel || 0
                const maoInventoryStock =
                    maoBOPISProductsAvailability.find(
                        (maoAvailability) => maoAvailability.productId === manufacturerSKU
                    )?.quantity || 0
                const storeInventory =
                    product?.c_availableForInStorePickup &&
                    maoInventoryStock &&
                    maoInventoryStock >= storeInventoryStock
                        ? maoInventoryStock
                        : storeInventoryStock

                productItemsToUpdate.push({
                    itemId: pli.itemId, // required field
                    quantity: pli.quantity, // required field
                    c_fromStoreId: store.ID || store.id,
                    c_storeInventory: storeInventory || 0
                })
            }
        })

        if (productItemsToUpdate.length) {
            updateItemsInBasket(productItemsToUpdate) // Not used in the App, so fire and forget
        }
    }

    useEffect(() => {
        const selectedShippingOption = basket?.shipments?.[0]?.shippingMethod?.c_storePickupEnabled
            ? 'storepickup'
            : 'delivery'

        if (maoBOPISProductsAvailability.length && selectedShippingOption === 'storepickup') {
            setStoreInLineItems()
        } else if (maoBOPISProductsAvailability.length && selectedShippingOption === 'delivery') {
            clearStoreInLineItems()
        }
    }, [store, basket?.shipments?.[0]?.shippingMethod?.id, maoBOPISProductsAvailability.length])

    /***************** Product Bundles ************************/
    const bundleChildVariantIds = []
    basket?.productItems?.forEach((productItem) => {
        productItem?.bundledProductItems?.forEach((childProduct) => {
            bundleChildVariantIds.push(childProduct.productId)
        })
    })

    const outOfStockProductsList = getOutOfStockProductsList(products, basket)
    const outOfStockGlobaleList = !countryISO || countryISO === 'US' ? [] : globaleUnavailableSKUs
    const globaleRestrictedList =
        !countryISO || countryISO === 'US' ? [] : getGlobaleRestrictedList(products, countryISO)
    const isCbOutOfStock = isAnyCbProductOutOfStock(products, basket)
    const {
        data: bundleChildProductData,
        isError: isBundleChildProductsError,
        error: bundleChildProductsError
    } = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds?.join(','),
                allImages: false,
                ...(uniqueInventoryIds ? {inventoryIds: uniqueInventoryIds} : {}),
                expand: ['availability', 'variations'],
                select: '(data.(id,inventory,inventories,master))'
            }
        },
        {
            enabled: bundleChildVariantIds?.length > 0,
            keepPreviousData: true,
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )
    const isBopisShipment = basket?.shipments?.[0]?.shippingMethod?.c_storePickupEnabled
    const hasBopisShipmentWithEngravingProduct =
        basket?.productItems?.some((item) => !!item?.c_driveProductID) && isBopisShipment
    // Aggregate error state/messages for cart or product fetching
    const hasCartError = Boolean(
        isBasketError ||
            isProductsError ||
            isBundleChildProductsError ||
            outOfStockProductsList?.length ||
            outOfStockGlobaleList?.length ||
            globaleRestrictedList?.length ||
            isCbOutOfStock ||
            hasBopisError ||
            hasBopisShipmentWithEngravingProduct
    )

    const errorSources = [
        {
            hasError: isBasketError,
            error: basketError,
            fallback: 'There was an error loading your cart.'
        },
        {
            hasError: isProductsError,
            error: productsError,
            fallback: 'There was an error loading product data.'
        },
        {
            hasError: isBundleChildProductsError,
            error: bundleChildProductsError,
            fallback: 'There was an error loading product data.'
        }
    ]

    const {configs} = commerceAPI.checkoutConfig || {}
    let isMobileOrderSummarySticky = false
    let enableWishlistInCart = false
    let cartRecommenderId = ''
    const {data: cbCallouts, isLoading: isCbCalloutsLoading} = useContentAsset(
        currencyCode &&
            `${!countryISO || countryISO === 'US' ? '' : 'globale-'}confidence-builder-callouts`
    )
    const philanthropyCalloutAssetId =
        currencyCode &&
        `${!countryISO || countryISO === 'US' ? '' : 'globale-'}philanthropy-callout`
    const {data: philanthropyCallout} = useContentAsset(philanthropyCalloutAssetId)
    try {
        if (configs) {
            const parsedConfigs = JSON.parse(configs)
            isMobileOrderSummarySticky = parsedConfigs.isMobileOrderSummarySticky
            enableWishlistInCart = parsedConfigs.enableWishlistInCart
            cartRecommenderId = parsedConfigs.cartRecommenderId
        }
    } catch (e) {
        console.error(e)
    }

    const stickyStyle = isMobileOrderSummarySticky
        ? cartStyles.mobileOrderSummaryContainer.sticky
        : {}

    const renderPhilanthropyCallout = (display) => {
        if (!philanthropyCallout?.contentAssetHTML) return null

        return (
            <Stack className="philanthropy-callout-stack" display={display}>
                <Box
                    data-asset={philanthropyCalloutAssetId}
                    dangerouslySetInnerHTML={{
                        __html: philanthropyCallout.contentAssetHTML
                    }}
                />
            </Stack>
        )
    }

    const showCbCalloutsSkeleton =
        !cbCallouts?.contentAssetHTML && (!currencyCode || isCbCalloutsLoading)
    const isCartLoading = isLoading || (isProductsLoading && !!productIds) || showPriceSkeleton

    // We use the `products` object to reference products by itemId instead of productId
    // Since with product bundles, even though the parent productId is the same,
    // variant selection of the bundle children can be different,
    // and require unique references to each product bundle
    const productsByItemId = useMemo(() => {
        const getLowestStockInfo = (
            parentProduct,
            productItem,
            bundleChildProductData,
            inventoryId = null
        ) => {
            const isDefaultInventory = !inventoryId
            const parentInventory = isDefaultInventory
                ? parentProduct.inventory
                : parentProduct.inventories?.find((inv) => inv.id === inventoryId)

            let lowestStockLevel = parentInventory?.stockLevel ?? Number.MAX_SAFE_INTEGER
            let productWithLowestInventory = ''

            productItem.bundledProductItems.forEach((bundleChild) => {
                const childProduct = bundleChildProductData[bundleChild.productId]
                const childInventory = isDefaultInventory
                    ? childProduct?.inventory
                    : childProduct?.inventories?.find((inv) => inv.id === inventoryId)
                const childStockLevel = childInventory?.stockLevel ?? Number.MAX_SAFE_INTEGER

                if (childStockLevel < lowestStockLevel) {
                    lowestStockLevel = childStockLevel
                    productWithLowestInventory = bundleChild.productName
                }
            })

            return {lowestStockLevel, productWithLowestInventory}
        }

        const updateProductsByItemId = {}
        basket?.productItems?.forEach((productItem) => {
            let currentProduct = products?.[productItem?.productId]

            if (currentProduct && productItem?.bundledProductItems && bundleChildProductData) {
                // Calculate and update the default inventory for the bundle.
                if (currentProduct.inventory) {
                    const {lowestStockLevel, productWithLowestInventory} = getLowestStockInfo(
                        currentProduct,
                        productItem,
                        bundleChildProductData
                    )
                    currentProduct = {
                        ...currentProduct,
                        inventory: {
                            ...currentProduct.inventory,
                            stockLevel: lowestStockLevel,
                            lowestStockLevelProductName: productWithLowestInventory
                        }
                    }
                }

                // Calculate and update in-store inventories for the bundle.
                if (currentProduct.inventories) {
                    const updatedInventories = currentProduct.inventories.map((inventory) => {
                        const {
                            lowestStockLevel: lowestInStoreStockLevel,
                            productWithLowestInventory: productWithLowestInventoryForStore
                        } = getLowestStockInfo(
                            currentProduct,
                            productItem,
                            bundleChildProductData,
                            inventory.id
                        )

                        return {
                            ...inventory,
                            stockLevel: lowestInStoreStockLevel,
                            lowestStockLevelProductName: productWithLowestInventoryForStore
                        }
                    })

                    currentProduct = {
                        ...currentProduct,
                        inventories: updatedInventories
                    }
                }
            }
            updateProductsByItemId[productItem.itemId] = currentProduct
        })
        return updateProductsByItemId
    }, [basket, products, bundleChildProductData])

    useEffect(() => {
        if (customerId && customer?.customerType === 'registered') {
            if (localStorage.getItem('ORCEError') === 'true') {
                setShowOrceDownWarning(true)
            } else {
                setShowOrceDownWarning(false)
            }
        }
    }, [customer])

    /*****************Basket Mutation************************/
    const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket')
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')
    /*****************Basket Mutation************************/

    const [selectedItem, setSelectedItem] = useState(undefined)
    const [localQuantity, setLocalQuantity] = useState({})
    const [localIsGiftItems, setLocalIsGiftItems] = useState({})
    const [isCartItemLoading, setCartItemLoading] = useState(false)
    const [isProcessingShippingMethods, setIsProcessingShippingMethods] = useState(false)
    const quantityDebouncersRef = useRef(new Map())
    const activeQuantityMutationsRef = useRef(0)

    const clearQuantityLoadingIfIdle = () => {
        if (activeQuantityMutationsRef.current === 0 && quantityDebouncersRef.current.size === 0) {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    const {isOpen, onOpen, onClose} = useDisclosure()
    const {formatMessage} = useIntl()
    const toast = useToast()
    const navigate = useNavigation()
    const modalProps = useDisclosure()
    const storeLocatorModal = useStoreLocatorModal()

    /******************* Assign Default Shipping Methods to Shipments *******************/
    // Assign default shipping methods to any shipments that don't have one
    // This runs when the basket is first loaded and whenever shipments change
    useEffect(() => {
        const assignDefaultShippingMethods = async () => {
            if (isProcessingShippingMethods || !basket?.basketId) {
                return
            }

            // Check if any shipments need shipping methods to avoid unnecessary processing
            const hasShipmentsWithoutMethod = basket.shipments?.some(
                (shipment) => !shipment.shippingMethod
            )

            if (!hasShipmentsWithoutMethod) {
                return
            }

            setIsProcessingShippingMethods(true)
            try {
                await updateShipmentsWithoutMethods()
            } catch (error) {
                console.error('Failed to assign default shipping methods:', error)
            } finally {
                setIsProcessingShippingMethods(false)
            }
        }

        assignDefaultShippingMethods()
    }, [basket?.basketId, basket?.shipments?.length, isProcessingShippingMethods])

    /************************* Error handling ***********************/
    const showError = () => {
        toast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    }
    /************************* Error handling ***********************/

    /***************** Loyalty birthday ************************/
    const {
        showBirthdayCard,
        birthdayCalloutHtml,
        isApplyingBirthdayCoupons,
        birthdayApplyError,
        handleApplyBirthdayCoupons
    } = useBirthdayCoupons({onError: showError})
    const birthdayPromoCardNode = showBirthdayCard && (
        <BirthdayPromoCard
            onApply={handleApplyBirthdayCoupons}
            isApplying={isApplyingBirthdayCoupons}
            calloutHtml={birthdayCalloutHtml}
            showOrceDownWarning={showOrceDownWarning}
            showError={birthdayApplyError}
        />
    )

    /**************** Wishlist ****************/
    const {data: wishlist} = useWishList()

    /**************** Monetate ****************/
    const monetate = useMonetate()
    const monetateCartSignature = useRef(null)

    // Retrack on every change to products, quantities, pricing, promos, gift options or
    // shipping method.
    useEffect(() => {
        if (isLoading) {
            return
        }

        // Unfiltered on purpose: SFRA's getCartRows iterates every line item, including
        // hidden fluid products and the ChangeUp donation.
        // No basket at all is the common first-visit empty cart (PWA Kit only creates a
        // basket on the first add to cart), so it has to be tracked like an empty basket
        // instead of bailing out and leaving the previous page's pageType in place.
        const signature = [
            basket?.basketId ?? 'no-basket',
            ...(basket?.productItems || []).map(
                (i) =>
                    `${i.productId}:${i.quantity}:${i.price}:${i.priceAfterOrderDiscount}:${
                        i.optionItems?.map((o) => `${o.optionValueId}@${o.price}`).join(',') || ''
                    }`
            ),
            ...(basket?.giftCertificateItems || []).map((g) => `${g.c_productID}:${g.amount}`),
            basket?.orderTotal,
            basket?.productSubTotal,
            (basket?.couponItems || []).map((c) => c.code).join(','),
            basket?.shipments?.[0]?.shippingMethod?.id,
            basket?.shipments?.[0]?.gift,
            // Customer and wishlist resolve after the basket, so they belong in the
            // signature: otherwise the corrected custom variables are never sent.
            isRegistered,
            wishlist?.customerProductListItems?.length ?? 'pending'
        ].join('|')

        if (signature === monetateCartSignature.current) {
            return
        }
        monetateCartSignature.current = signature

        const isEmptyCart = !basket?.productItems?.length && !basket?.giftCertificateItems?.length

        monetate.monetateTrackPage(
            isEmptyCart ? 'emptyCart' : 'cart',
            [CART_TITLE],
            basket?.productItems || [],
            isRegistered,
            wishlist?.customerProductListItems?.length || 0,
            null,
            null,
            null,
            basket?.giftCertificateItems
        )
    }, [basket, isLoading, isRegistered, wishlist])

    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const handleAddToWishlist = async (product) => {
        try {
            if (!customerId || !wishlist) {
                return
            }

            const isItemInWishlist = wishlist?.customerProductListItems?.find(
                (i) => i.productId === product?.id
            )

            if (!isItemInWishlist) {
                await createCustomerProductListItem.mutateAsync({
                    parameters: {
                        listId: wishlist.id,
                        customerId
                    },
                    body: {
                        // NOTE: APi does not respect quantity, it always adds 1
                        quantity: product.quantity,
                        productId: product.productId,
                        public: false,
                        priority: 1,
                        type: 'product'
                    }
                })
                handleRemoveItem(product)
                return true
            } else {
                toast({
                    title: formatMessage(TOAST_MESSAGE_ALREADY_IN_WISHLIST),
                    status: 'info',
                    action: (
                        <Button variant="link" onClick={() => navigate('/account/wishlist')}>
                            {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
                        </Button>
                    )
                })
                return false
            }
        } catch {
            showError()
            return false
        }
    }
    /**************** Wishlist ****************/

    /***************************** Update Cart **************************/
    const handleUpdateCart = async (variant, quantity) => {
        // close the modal before handle the change
        onClose()
        // using try-catch is better than using onError callback since we have many mutation calls logic here
        setShowPriceSkeleton(true)
        try {
            setCartItemLoading(true)
            const productIds = basket.productItems.map(({productId}) => productId)

            // The user is selecting different variant, and it has not existed in basket
            if (selectedItem.id !== variant.productId && !productIds.includes(variant.productId)) {
                const item = {
                    productId: variant.productId,
                    quantity
                }
                const response = await updateItemInBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        itemId: selectedItem.itemId
                    },
                    body: item
                })
                if (countryISO && countryISO !== 'US') {
                    await getGlobalePrices()
                } else {
                    setShowPriceSkeleton(false)
                }
                return response
            }

            // The user is selecting different variant, and it has existed in basket
            // remove this item in the basket, change the quantity for the new selected variant in the basket
            if (selectedItem.id !== variant.productId && productIds.includes(variant.productId)) {
                await removeItemFromBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        itemId: selectedItem.itemId
                    }
                })
                const basketItem = basket.productItems.find(
                    ({productId}) => productId === variant.productId
                )
                const newQuantity = quantity + basketItem.quantity
                return await changeItemQuantity(newQuantity, basketItem)
            }

            // the user only changes quantity of the same variant
            if (selectedItem.quantity !== quantity) {
                return await changeItemQuantity(quantity, selectedItem)
            }
        } catch {
            showError()
            setShowPriceSkeleton(false)
        } finally {
            setShowPriceSkeleton(false)
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    const handleIsAGiftChange = async (product, checked) => {
        try {
            const previousVal = localIsGiftItems[product.itemId]
            setLocalIsGiftItems({
                ...localIsGiftItems,
                [product.itemId]: checked
            })
            setCartItemLoading(true)
            setSelectedItem(product)
            await updateItemInBasketMutation.mutateAsync(
                {
                    parameters: {basketId: basket?.basketId, itemId: product.itemId},
                    body: {
                        productId: product.id,
                        quantity: parseInt(product.quantity),
                        gift: checked
                    }
                },
                {
                    onSettled: () => {
                        // reset the state
                        setCartItemLoading(false)
                        setSelectedItem(undefined)
                    },
                    onSuccess: () => {
                        setLocalIsGiftItems({...localIsGiftItems, [product.itemId]: undefined})
                    },
                    onError: () => {
                        // reset the quantity to the previous value
                        setLocalIsGiftItems({...localIsGiftItems, [product.itemId]: previousVal})
                        showError()
                    }
                }
            )
        } catch (e) {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    /***************************** Update Cart **************************/

    /***************************** Update quantity **************************/
    const changeItemQuantityHandlerRef = useRef()
    changeItemQuantityHandlerRef.current = async (quantity, product, indexPosition) => {
        activeQuantityMutationsRef.current += 1
        const isRemoveAction = quantity < product.quantity
        // This local state allows the dropdown to show the desired quantity
        // while the API call to update it is happening.
        const previousQuantity = localQuantity[product.itemId]
        setLocalQuantity((current) => ({...current, [product.itemId]: quantity}))

        const pwaProduct = products?.[product.productId]
        await updateItemInBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket?.basketId, itemId: product.itemId},
                body: {
                    productId: product.id,
                    quantity: parseInt(quantity)
                }
            },
            {
                onSettled: () => {
                    activeQuantityMutationsRef.current = Math.max(
                        0,
                        activeQuantityMutationsRef.current - 1
                    )
                    clearQuantityLoadingIfIdle()
                    delete window.updatedIndex
                },
                onSuccess: () => {
                    setLocalQuantity((current) => ({
                        ...current,
                        [product.itemId]: undefined
                    }))
                    if (isRemoveAction) {
                        // Report the units removed, not the quantity left on the line.
                        tagManager.removeFromCart(
                            product,
                            pwaProduct,
                            product.quantity - parseInt(quantity),
                            basket?.couponItems?.map((c) => c.code).join(',') || ''
                        )
                    } else {
                        // Report the units added, not the new line quantity. The quantity picker
                        // no longer publishes window.updatedItemPrice/Qty (DEM-17708), so the
                        // delta has to ride along on the product handed to the builder.
                        tagManager.addToCartGA4({
                            itemsAdded: [
                                {
                                    product: {
                                        ...pwaProduct,
                                        quantity: parseInt(quantity) - product.quantity
                                    }
                                }
                            ],
                            method: 'cart',
                            currency: globaleCurrency,
                            sorting: undefined,
                            indexPosition,
                            listName: 'cart',
                            listId: 'cart'
                        })
                    }
                },
                onError: async (error) => {
                    // reset the quantity to the previous value
                    setLocalQuantity((current) => ({
                        ...current,
                        [product.itemId]: previousQuantity
                    }))
                    const json = await error.response.json()
                    if (json?.title !== 'Invalid Product Item Id') {
                        showError()
                    }
                    delete window.updatedItemPrice
                    delete window.updatedItemQty
                    delete window.updatedIndex
                }
            }
        )

        if (countryISO && countryISO !== 'US') {
            await getGlobalePrices()
        }
    }
    const changeItemQuantity = useMemo(
        () => (quantity, product) => {
            let debouncedUpdate = quantityDebouncersRef.current.get(product.itemId)
            if (!debouncedUpdate) {
                debouncedUpdate = debounce((...args) => {
                    quantityDebouncersRef.current.delete(product.itemId)
                    return changeItemQuantityHandlerRef.current(...args)
                }, DEBOUNCE_WAIT)
                quantityDebouncersRef.current.set(product.itemId, debouncedUpdate)
            }
            return debouncedUpdate(quantity, product, window.updatedIndex)
        },
        []
    )

    useEffect(
        () => () => {
            Array.from(quantityDebouncersRef.current.values()).forEach((debouncedUpdate) =>
                debouncedUpdate.flush()
            )
            quantityDebouncersRef.current.clear()
        },
        []
    )

    const handleChangeItemQuantity = async (product, value) => {
        const productItemInventory =
            productsByItemId?.[product.itemId]?.inventories?.find(
                (inventory) => inventory.id === product.inventoryId
            ) || productsByItemId?.[product.itemId]?.inventory
        const stockLevel = productItemInventory?.stockLevel ?? 1

        // Handle removing of the items when 0 is selected.
        if (value === 0) {
            // Flush last call to keep ui in sync with data.
            quantityDebouncersRef.current.get(product.itemId)?.flush()

            // Keep removal work out of the interaction task so click feedback paints first.
            scheduleAfterPaint(() => handleRemoveItem(product))
            // Return false as 0 isn't valid section.
            return false
        }

        // Only replace a pending update for this cart line.
        quantityDebouncersRef.current.get(product.itemId)?.cancel()
        quantityDebouncersRef.current.delete(product.itemId)

        // Allow use to selected values above the inventory.
        if (value > stockLevel) {
            clearQuantityLoadingIfIdle()
            return true
        }

        if (value === product.quantity) {
            clearQuantityLoadingIfIdle()
            return true
        }

        // Show feedback without making the cart-wide render part of the interaction task.
        startTransition(() => {
            setCartItemLoading(true)
            setSelectedItem(product)
        })

        // Keep the API request debounced while the spinner is already visible.
        changeItemQuantity(value, product)
        return true
    }
    /***************************** Update quantity **************************/

    /***************************** Remove Item from basket **************************/
    const handleRemoveItem = async (product) => {
        setShowPriceSkeleton(true)
        setSelectedItem(product)
        setCartItemLoading(true)

        // Check if this is a bonus product that needs bulk removal
        if (product.bonusProductLineItem) {
            // Find all bonus product items that should be removed together
            const itemsToRemove = findAllBonusProductItemsToRemove(basket, product)

            if (itemsToRemove.length > 1) {
                // Set removing state for UI feedback
                const itemIdsToRemove = itemsToRemove.map((item) => item.itemId)
                setRemovingItemIds(itemIdsToRemove)

                // Track removal progress
                let index = 0

                // Sequential removal function to avoid race conditions
                const removeNextItem = async () => {
                    if (index >= itemsToRemove.length) {
                        // All items processed
                        setCartItemLoading(false)
                        setSelectedItem(undefined)
                        setRemovingItemIds([])
                        return
                    }

                    const currentItem = itemsToRemove[index]

                    await removeItemFromBasketMutation.mutate(
                        {
                            parameters: {basketId: basket.basketId, itemId: currentItem.itemId}
                        },
                        {
                            onSuccess: () => {
                                tagManager.removeFromCart(
                                    product,
                                    products?.[product.productId],
                                    product.quantity,
                                    basket?.couponItems?.map((c) => c.code).join(',') || ''
                                )
                            },
                            onSettled: async () => {
                                delete window.updatedIndex
                                index++
                                // Process next item after this one settles
                                setTimeout(removeNextItem, 100)
                                if (countryISO && countryISO !== 'US') {
                                    await getGlobalePrices()
                                } else {
                                    setShowPriceSkeleton(false)
                                }
                            },
                            onError: (error) => {
                                console.error('Item removal error:', error)
                            }
                        }
                    )
                }

                removeNextItem()
            } else {
                // Single bonus product item
                await removeItemFromBasketMutation.mutate(
                    {
                        parameters: {basketId: basket.basketId, itemId: product.itemId}
                    },
                    {
                        onSuccess: () => {
                            tagManager.removeFromCart(
                                product,
                                products?.[product.productId],
                                product.quantity,
                                basket?.couponItems?.map((c) => c.code).join(',') || ''
                            )
                        },
                        onSettled: async () => {
                            delete window.updatedIndex
                            setCartItemLoading(false)
                            setSelectedItem(undefined)
                            if (countryISO && countryISO !== 'US') {
                                await getGlobalePrices()
                            } else {
                                setShowPriceSkeleton(false)
                            }
                        },
                        onError: async (error) => {
                            console.error('Bonus product removal error:', error)
                            const json = await error.response.json()
                            if (json?.title !== 'Invalid Product Item Id') {
                                showError()
                            }
                        }
                    }
                )
            }
        } else {
            // Regular (non-bonus) product removal
            await removeItemFromBasketMutation.mutate(
                {
                    parameters: {basketId: basket.basketId, itemId: product.itemId}
                },
                {
                    onSuccess: () => {
                        tagManager.removeFromCart(
                            product,
                            products?.[product.productId],
                            product.quantity,
                            basket?.couponItems?.map((c) => c.code).join(',') || ''
                        )
                    },
                    onSettled: async () => {
                        delete window.updatedIndex
                        setCartItemLoading(false)
                        setSelectedItem(undefined)
                        if (countryISO && countryISO !== 'US') {
                            await getGlobalePrices()
                        } else {
                            setShowPriceSkeleton(false)
                        }
                    },
                    onError: async (error) => {
                        console.error('Product removal error:', error)
                        const json = await error.response.json()
                        if (json?.title !== 'Invalid Product Item Id') {
                            showError()
                        }
                    }
                }
            )
        }
    }

    // Create shipment-specific data, but group all qualifying products together for bonus product grouping
    const shipmentData = useMemo(() => {
        if (!basket?.shipments?.length) return []

        const pickupShipments = []
        const deliveryShipments = []

        // Separate pickup and delivery shipments
        basket.shipments.forEach((shipment) => {
            const isPickupOrder = storeLocatorEnabled && isPickupShipment(shipment)
            const storeId = shipment?.c_fromStoreId
            const store = storeData?.data?.find((store) => store.id === storeId)

            // Filter products for this shipment
            const shipmentProducts =
                basket.productItems?.filter(
                    (productItem) => productItem.shipmentId === shipment.shipmentId
                ) || []

            // Categorize products into regular and bonus for this shipment
            const categorizedProducts = shipmentProducts.reduce(
                (acc, productItem) => {
                    // All bonus products go to bonusProducts array (both grouped and orphaned)
                    if (productItem.bonusProductLineItem) {
                        acc.bonusProducts.push(productItem)
                    } else {
                        // Only non-bonus products go to regular products
                        acc.regularProducts.push(productItem)
                    }
                    return acc
                },
                {regularProducts: [], bonusProducts: []}
            )

            const shipmentData = {
                shipment,
                isPickupOrder,
                store,
                categorizedProducts,
                itemsInShipment:
                    categorizedProducts.regularProducts.length +
                    categorizedProducts.bonusProducts.length
            }

            // Only add shipments that have regular products
            if (shipmentData.categorizedProducts.regularProducts.length > 0) {
                if (isPickupOrder) {
                    pickupShipments.push(shipmentData)
                } else {
                    deliveryShipments.push(shipmentData)
                }
            }
        })

        const result = [...pickupShipments]

        // Combine all delivery shipments into one for display purposes
        if (deliveryShipments.length > 0) {
            const combinedDeliveryProducts = deliveryShipments.reduce(
                (acc, shipmentData) => {
                    acc.regularProducts.push(...shipmentData.categorizedProducts.regularProducts)
                    acc.bonusProducts.push(...shipmentData.categorizedProducts.bonusProducts)
                    return acc
                },
                {regularProducts: [], bonusProducts: []}
            )

            result.push({
                shipment: null, // No specific shipment for combined delivery
                isPickupOrder: false,
                store: null, // No specific store for combined delivery
                categorizedProducts: combinedDeliveryProducts,
                itemsInShipment:
                    combinedDeliveryProducts.regularProducts.length +
                    combinedDeliveryProducts.bonusProducts.length
            })
        }

        return result
    }, [basket?.shipments, basket?.productItems, storeData])

    const giftCertificateItems = () => {
        if (!basket?.giftCertificateItems?.length) return []

        return basket.giftCertificateItems
    }

    // Helper function to get shipment info for a product
    const getShipmentInfoForProduct = (productItem) => {
        const shipment = basket?.shipments?.find((s) => s.shipmentId === productItem.shipmentId)
        if (!shipment) return null

        const isPickupOrder = storeLocatorEnabled && isPickupShipment(shipment)
        const storeId = shipment?.c_fromStoreId
        const store = storeData?.data?.find((store) => store.id === storeId)

        return {
            shipment,
            isPickupOrder,
            store
        }
    }

    /***************************** Delivery Options **************************/

    const onDeliveryOptionChange = async (productItem, selectedDeliveryOption) => {
        try {
            setCartItemLoading(true)
            setSelectedItem(productItem)

            const selectedPickup = selectedDeliveryOption === DELIVERY_OPTIONS.PICKUP

            // If the user selects pickup and no store is selected, open the store locator modal
            if (selectedPickup && !selectedStore) {
                storeLocatorModal.onOpen()
                return
            }

            const productData = products?.[productItem.productId]
            const defaultInventoryId = productData?.inventory?.id

            if (!defaultInventoryId) {
                throw new Error(`No inventory ID found for product ${productItem.productId}`)
            }

            await updateDeliveryOption(
                productItem,
                selectedPickup,
                selectedStore,
                defaultInventoryId
            )
        } catch (error) {
            console.error('Error changing delivery option:', error)
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    // Function to render deliveryActions
    const renderDeliveryActions = (productItem, shipmentInfo) => {
        const showDeliveryOptions = storeLocatorEnabled && multishipEnabled
        if (!showDeliveryOptions) {
            return null
        }

        // Check if this product has bonus products associated with it
        // If it does, hide the delivery group selector
        const hasBonusProducts =
            getBonusProductsForSpecificCartItem(
                basket,
                productItem,
                productsWithPromotions,
                ruleBasedQualifyingProductsMap
            ).length > 0

        if (hasBonusProducts) {
            return null
        }

        const deliveryOption = shipmentInfo.isPickupOrder
            ? DELIVERY_OPTIONS.PICKUP
            : DELIVERY_OPTIONS.DELIVERY

        const selectedStoreInventoryAvailable =
            productsByItemId?.[productItem.itemId]?.inventories?.find(
                (inventory) => inventory.id === selectedInventoryId
            )?.stockLevel >= productItem.quantity
        const defaultInventoryAvailable =
            productsByItemId?.[productItem.itemId]?.inventory?.stockLevel >= productItem.quantity
        const isPickupDisabled = !shipmentInfo.isPickupOrder && !selectedStoreInventoryAvailable
        const isShipDisabled = shipmentInfo.isPickupOrder && !defaultInventoryAvailable

        return (
            <PickupOrDelivery
                isPickupDisabled={isPickupDisabled}
                isShipDisabled={isShipDisabled}
                value={deliveryOption}
                onChange={(selectedValue) => onDeliveryOptionChange(productItem, selectedValue)}
            />
        )
    }

    // Function to render secondary actions for product items
    const renderSecondaryActions = ({isAGift}) => (
        <CartSecondaryButtonGroup
            isAGift={isAGift}
            onIsAGiftChange={handleIsAGiftChange}
            onAddToWishlistClick={handleAddToWishlist}
            onEditClick={(product) => {
                scheduleAfterPaint(() => {
                    setSelectedItem(product)
                    onOpen()
                })
            }}
            onRemoveItemClick={handleRemoveItem}
        />
    )

    useEffect(() => {
        const charitableDonationItem = getCharitableDonationItem(basket, changeUpProductID)
        // Remove 'Charitable Donation' if it is the only one item left in the basket.
        if (
            basket?.productItems?.length === 1 &&
            basket?.productItems?.[0]?.productId === changeUpProductID
        ) {
            handleRemoveItem(charitableDonationItem)
        }

        // Remove 'Charitable Donation' if the country is not US.
        if (countryISO && currencyCode && countryISO !== 'US' && charitableDonationItem) {
            // Note: 'countryISO' & 'currencyCode' should not be null before we remove the 'Charitable Donation'.
            handleRemoveItem(charitableDonationItem)
        }
    }, [basket?.productItems?.length, countryISO, currencyCode])

    const validateOrderLimit = async () => {
        if (products) {
            Object.values(products).forEach(async (p) => {
                const item = basket.productItems.find((i) => i.productId === p.id)
                if (item?.quantity > p.c_orderLimit) {
                    await handleChangeItemQuantity(item, p.c_orderLimit)
                }
            })
        }
    }

    useEffect(() => {
        try {
            validateOrderLimit()
        } catch (e) {
            console.error("Error occurred in 'validateOrderLimit'", e.message)
        }
    }, [basket, products])

    useEffect(() => {
        /*
            Since the DOM loads asynchronously, ensure that if the user logs in from the
            “Save to Wishlist” button, we add the basket item to the wishlist and then remove item
        */
        const url = new URL(window.location.href)
        if (!url.searchParams.has('pid') || isLoading) return

        window.setTimeout(() => {
            const pid = url.searchParams.get('pid')
            const btn = document.querySelector(
                `.sf-cart-item[data-testid="sf-cart-item-${pid}"] .wishlist-button`
            )
            if (btn) btn.click()

            url.searchParams.delete('pid')
            window.history.replaceState({}, '', url.pathname + url.search)
        }, 700)
    }, [isLoading])

    const promotionIds = [
        ...new Set(
            Object.values(products || {}).flatMap((p) =>
                Array.isArray(p?.productPromotions)
                    ? p.productPromotions.map((pr) => pr?.promotionId).filter(Boolean)
                    : []
            )
        )
    ]
    const meterPromos = (promotionIds || []).filter(
        (promo) => typeof promo === 'string' && promo.includes('BMSM')
    )
    const enabledPromotion = !isServer && !!meterPromos?.length
    const {data: promotionsDetails, isLoading: isPromoLoading} = usePromotions(
        {
            parameters: {
                ids: meterPromos
            }
        },
        {
            enabled: enabledPromotion
        }
    )
    const bmsmDetails = promotionsDetails?.data?.find(function (data) {
        return (
            data?.c_bmsmAndGwpMeterConfig &&
            !!data?.c_enableProgressMeter &&
            data?.id?.includes('BMSM')
        )
    })

    const updateShipmentForBasketMutation = useShopperBasketsMutation('updateShipmentForBasket')
    const setShipmentGift = async (formValues) => {
        try {
            const body = formValues.giftWrapCheckBox
                ? {
                      gift: true,
                      giftMessage: formValues.giftWrapMessage
                  }
                : {gift: false, giftMessage: ''}

            await updateShipmentForBasketMutation.mutateAsync({
                parameters: {
                    basketId: basket?.basketId,
                    shipmentId: basket?.shipments?.[0]?.shipmentId
                },
                body: body
            })
        } catch (e) {
            console.error('Unable to mutate shipment', e.message)
        }
    }

    const [dataLayerLoaded, setDataLayerLoaded] = useState(false)
    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: !onClient()
        })
    const customerClientId = useCustomerId()
    const searchParams = useSearchParams({limit: 100, offset: 0})
    const {limit, offset} = searchParams[0]
    const {data: {data: orders} = {}, isLoading: isOrdersLoading} = useCustomerOrders(
        {
            parameters: {customerId: customerClientId, limit, offset}
        },
        {enabled: onClient() && !!customerClientId && customer?.customerType === 'registered'}
    )
    useEffect(() => {
        if (!!customerClientId && customer?.customerType === 'registered' && isOrdersLoading) {
            return
        }
        // 'CookieStorage' takes some miliseconds to fetch cookies, that's why we've used 'useEffect'
        const city = cookie.get('city')
        const country = cookie.get('countryCode')
        const state = cookie.get('state')
        if (!country || showPriceSkeleton) return
        if (
            ((customer?.customerType === 'registered' && customer?.email) ||
                customer?.customerType === 'guest') &&
            !isLoading &&
            !dataLayerLoaded
        ) {
            var breadCrumb = ''
            tagManager.dataLayerLoad(
                'cart',
                isRegistered,
                breadCrumb,
                countryISO,
                currencyCode,
                customer,
                city,
                country,
                state,
                null,
                null,
                'cart',
                orders?.length
            )
            setDataLayerLoaded(true)
        }
    }, [customer?.customerType, customer?.email, isLoading, cookie, showPriceSkeleton])

    const giftWrapForm = useForm()

    const set2DayShippingMethod = async (shipment) => {
        const authToken = await getTokenWhenReady()
        const {applicableShippingMethods} = await fetchShippingMethods(
            basket?.basketId,
            site,
            authToken
        )
        const isTwoDayApplicable = Boolean(
            applicableShippingMethods?.some((method) => method.id === TWO_DAY_METHOD_ID)
        )
        if (isTwoDayApplicable) {
            updateShippingMethod.mutateAsync({
                parameters: {
                    basketId: basket.basketId,
                    shipmentId: shipment.shipmentId
                },
                body: {
                    id: TWO_DAY_METHOD_ID
                }
            })
            localStorage.removeItem('customerSelectedShippingMethod')
            localStorage.setItem('2DayApplied', true)
        }
    }

    useEffect(() => {
        window.pwaProducts = products
        const shipment = basket?.shipments?.[0]
        if (!shipment || !products || shipment?.shippingMethod?.id === TWO_DAY_METHOD_ID) {
            return
        }
        const fineJewelryExists = isFineJewelryExists(products)
        if (
            shipment &&
            localStorage.getItem('customerSelectedShippingMethod') !== 'true' &&
            !localStorage.getItem('2DayApplied') &&
            fineJewelryExists &&
            !isBopisShipment &&
            basket?.shipments?.[0]?.shippingAddress
        ) {
            set2DayShippingMethod(shipment)
        }
    }, [basket?.shipments?.[0]?.shippingAddress, products])

    /********* Rendering  UI **********/
    if (isLoading) {
        return <CartSkeleton />
    }
    if (!isLoading && !basket?.productItems?.length && !basket?.giftCertificateItems?.length) {
        return <EmptyCart isRegistered={isRegistered} />
    }

    const isCustomerLoggedIn = !customer?.isGuest
    const isEGiftCardOnly = isEGiftCardInBasket(basket)
    return (
        <Box
            backgroundColor="white"
            flex="1"
            data-testid="sf-cart-container"
            className="sf-cart-container"
        >
            <Helmet>
                <title>{CART_TITLE}</title>
            </Helmet>
            <Heading as="h1" srOnly>
                <FormattedMessage defaultMessage="Shopping Cart" id="cart.title.shopping_cart" />
            </Heading>
            <Container className="cart-container" {...cartStyles.cartContainer}>
                {isBopisLoading && <LoadingSpinner position="fixed" top="auto" />}
                <Stack className="grid-stack-wrapper">
                    <Stack
                        className="grid-stack"
                        sx={isMobileOrderSummarySticky ? stickyExpandedSummaryStyles : undefined}
                    >
                        <Box
                            className="mobile-summary"
                            {...cartStyles.mobileOrderSummaryContainer}
                            {...stickyStyle}
                        >
                            <CartCta
                                hasError={hasCartError}
                                errorSources={errorSources}
                                isLoading={isCartLoading}
                                showExpressCheckout={false}
                                isTopCartCta={true}
                            />
                            <OrderSummary
                                showPromoCodeForm={true}
                                currencySymbol={currencySymbol}
                                showPriceSkeleton={showPriceSkeleton}
                                isEstimate={false}
                                isMobileTopSummary={true}
                                birthdayPromoCard={birthdayPromoCardNode}
                            />
                        </Box>
                        {/* Keep the banner outside the sticky container so it scrolls normally. */}
                        {countryISO === 'US' ? (
                            <Box className="shipping-banner" display={{base: 'block', lg: 'none'}}>
                                <FreeShippingProgressBar />
                            </Box>
                        ) : !countryISO && resolvedCountryISO === 'US' ? (
                            <Box className="shipping-banner" display={{base: 'block', lg: 'none'}}>
                                <FreeShippingProgressSkeleton />
                            </Box>
                        ) : null}
                        <Grid
                            className="grid-item-wrapper"
                            templateColumns={{
                                base: '1fr',
                                lg: 'minmax(520px, 800px) 394px',
                                '2lg': 'minmax(540px, 800px) 414px'
                            }}
                            gap={{base: 10, lg: '20px', '3lg': '66px'}}
                            justifyContent="space-between"
                        >
                            <GridItem
                                className="grid-item"
                                display="grid"
                                gap="30px"
                                alignContent="start"
                            >
                                {enablePurpleDot && <CartPurpledotBanner />}
                                {bmsmDetails && (
                                    <BMSMMeter
                                        size="cart"
                                        promoDetail={bmsmDetails}
                                        isPromoLoading={isPromoLoading}
                                    />
                                )}
                                <GwpPwpCartCallout basket={basket} />
                                <CartEndorsementCallout />
                                <CartTitle />
                                <Stack spacing={0} className="group-stack">
                                    {shipmentData.map((shipmentInfo) => (
                                        <Box
                                            key={
                                                shipmentInfo.shipment?.shipmentId ||
                                                'combined-delivery'
                                            }
                                            bg="white"
                                            className="group-box"
                                            gap="0px"
                                            overflow="hidden"
                                        >
                                            <Stack gap={0}>
                                                {/* Render grouped bonus products from this shipment */}
                                                {shipmentInfo.categorizedProducts.bonusProducts
                                                    ?.filter(
                                                        (productItem) =>
                                                            productItem.bonusDiscountLineItemId
                                                    )
                                                    ?.map((productItem, index) => (
                                                        <ProductItemList
                                                            index={index}
                                                            currencySymbol={currencySymbol}
                                                            showPriceSkeleton={showPriceSkeleton}
                                                            globaleUnavailableSKUs={
                                                                globaleUnavailableSKUs
                                                            }
                                                            key={productItem.itemId}
                                                            oosBopisSkus={oosBopisSkus}
                                                            productItems={[productItem]}
                                                            productsByItemId={productsByItemId}
                                                            isProductsLoading={
                                                                isProductsLoading && !!productIds
                                                            }
                                                            localQuantity={localQuantity}
                                                            localIsGiftItems={localIsGiftItems}
                                                            isCartItemLoading={isCartItemLoading}
                                                            selectedItem={selectedItem}
                                                            removingItemIds={removingItemIds}
                                                            onItemQuantityChange={
                                                                handleChangeItemQuantity
                                                            }
                                                            onRemoveItemClick={handleRemoveItem}
                                                            renderSecondaryActions={
                                                                renderSecondaryActions
                                                            }
                                                            getShipmentInfoForProduct={
                                                                getShipmentInfoForProduct
                                                            }
                                                            renderDeliveryActions={(
                                                                productItem
                                                            ) => {
                                                                const productShipmentInfo =
                                                                    getShipmentInfoForProduct(
                                                                        productItem
                                                                    )
                                                                return productShipmentInfo
                                                                    ? renderDeliveryActions(
                                                                          productItem,
                                                                          productShipmentInfo
                                                                      )
                                                                    : null
                                                            }}
                                                            outOfStockProductsList={
                                                                outOfStockProductsList
                                                            }
                                                            isBonus={true}
                                                            totalRegularProducts={
                                                                shipmentInfo.categorizedProducts.bonusProducts?.filter(
                                                                    (productItem) =>
                                                                        productItem.bonusDiscountLineItemId
                                                                )?.length
                                                            }
                                                            giftCertificateItems={giftCertificateItems()}
                                                            totalBonusProducts={
                                                                shipmentInfo.categorizedProducts.bonusProducts?.filter(
                                                                    (productItem) =>
                                                                        productItem.bonusDiscountLineItemId
                                                                )?.length
                                                            }
                                                        />
                                                    ))}
                                                {/* Render all cart items in simple layout */}
                                                {shipmentInfo.categorizedProducts.regularProducts?.map(
                                                    (productItem, index) => (
                                                        <ProductItemList
                                                            index={index}
                                                            currencySymbol={currencySymbol}
                                                            showPriceSkeleton={showPriceSkeleton}
                                                            globaleUnavailableSKUs={
                                                                globaleUnavailableSKUs
                                                            }
                                                            key={productItem.itemId}
                                                            productItems={[productItem]}
                                                            oosBopisSkus={oosBopisSkus}
                                                            productsByItemId={productsByItemId}
                                                            isProductsLoading={
                                                                isProductsLoading && !!productIds
                                                            }
                                                            localQuantity={localQuantity}
                                                            localIsGiftItems={localIsGiftItems}
                                                            isCartItemLoading={isCartItemLoading}
                                                            selectedItem={selectedItem}
                                                            removingItemIds={removingItemIds}
                                                            onItemQuantityChange={
                                                                handleChangeItemQuantity
                                                            }
                                                            onRemoveItemClick={handleRemoveItem}
                                                            renderSecondaryActions={
                                                                renderSecondaryActions
                                                            }
                                                            getShipmentInfoForProduct={
                                                                getShipmentInfoForProduct
                                                            }
                                                            renderDeliveryActions={(productItem) =>
                                                                renderDeliveryActions(
                                                                    productItem,
                                                                    shipmentInfo
                                                                )
                                                            }
                                                            outOfStockProductsList={
                                                                outOfStockProductsList
                                                            }
                                                            totalRegularProducts={
                                                                shipmentInfo?.categorizedProducts
                                                                    ?.regularProducts?.length
                                                            }
                                                            totalBonusProducts={
                                                                shipmentInfo.categorizedProducts.bonusProducts?.filter(
                                                                    (productItem) =>
                                                                        productItem.bonusDiscountLineItemId
                                                                )?.length
                                                            }
                                                            giftCertificateItems={giftCertificateItems()}
                                                            isBonus={false}
                                                        />
                                                    )
                                                )}
                                            </Stack>
                                        </Box>
                                    ))}
                                    <Box
                                        key={'gift-certificate'}
                                        bg="white"
                                        className="group-box"
                                        gap="0px"
                                        overflow="hidden"
                                    >
                                        <GiftCertificateItems
                                            giftCertificateItems={giftCertificateItems()}
                                            basket={basket}
                                            products={products}
                                            shipmentData={shipmentData}
                                        />
                                    </Box>
                                </Stack>
                                {renderPhilanthropyCallout({base: 'none', lg: 'block'})}
                                {enableWishlistInCart && countryISO === 'US' && (
                                    <Box
                                        width="100%"
                                        overflow="hidden"
                                        display={{base: 'none', lg: 'block'}}
                                    >
                                        <WishlistInCart />
                                    </Box>
                                )}
                                <PwpCarousel basket={basket} />

                                {cartRecommenderId && cartRecommenderId !== '' && (
                                    <Stack
                                        width="100%"
                                        overflow="hidden"
                                        className="cart-recommendations-wrapper"
                                        display={{base: 'none', lg: 'block'}}
                                    >
                                        <CartRecommendation
                                            cartRecommenderId={cartRecommenderId}
                                            openQuickAddModal={true}
                                        />
                                    </Stack>
                                )}

                                {isOpen && !selectedItem.bundledProductItems && (
                                    <ProductViewModal
                                        isOpen={isOpen}
                                        onOpen={onOpen}
                                        onClose={onClose}
                                        product={selectedItem}
                                        updateCart={(variant, quantity) =>
                                            handleUpdateCart(variant, quantity)
                                        }
                                        showDeliveryOptions={false}
                                    />
                                )}
                            </GridItem>
                            <GridItem
                                className="summary-grid-item"
                                gap="30px"
                                display="grid"
                                alignContent="start"
                            >
                                <Stack
                                    spacing={4}
                                    backgroundColor="gray.100"
                                    padding="15px"
                                    {...cartStyles.mobileOrderSummaryContainer.inline}
                                    display={{base: 'none', lg: 'flex'}}
                                >
                                    <CartCta
                                        isDesktopOnly={true}
                                        hasError={hasCartError}
                                        errorSources={errorSources}
                                        isLoading={isCartLoading}
                                        showExpressCheckout={false}
                                    />
                                    {countryISO === 'US' && (
                                        <FreeShippingProgressBar isDesktop={true} />
                                    )}
                                    <OrderSummary
                                        showPromoCodeForm={true}
                                        currencySymbol={currencySymbol}
                                        showPriceSkeleton={showPriceSkeleton}
                                        isEstimate={false}
                                        isOrderSummaryExpanded={true}
                                        birthdayPromoCard={birthdayPromoCardNode}
                                    />
                                </Stack>
                                {countryISO === 'US' ? (
                                    <Stack className="gift-wrap-box">
                                        <GiftWrapBox
                                            form={giftWrapForm}
                                            shipment={basket?.shipments?.[0]}
                                            setShipmentGift={setShipmentGift}
                                            isCheckout={false}
                                            isCart={true}
                                        />
                                    </Stack>
                                ) : !countryISO && resolvedCountryISO === 'US' ? (
                                    <Skeleton height="70px" borderRadius="3px" />
                                ) : null}
                                {(showCbCalloutsSkeleton || cbCallouts?.contentAssetHTML) && (
                                    <Stack>
                                        {showCbCalloutsSkeleton ? (
                                            <Skeleton aspectRatio="23 / 6" borderRadius="md" />
                                        ) : (
                                            <Box
                                                data-asset={`${
                                                    countryISO === 'US' ? '' : 'globale-'
                                                }confidence-builder-callouts`}
                                                dangerouslySetInnerHTML={{
                                                    __html: cbCallouts?.contentAssetHTML
                                                }}
                                            />
                                        )}
                                    </Stack>
                                )}
                                <Stack className="globale-cart-stack">
                                    <CartDeliveryOptions
                                        selectedDelivery={selectedDelivery}
                                        setDelivery={setDelivery}
                                        setShowPriceSkeleton={(bool) => {
                                            setShowPriceSkeleton(bool)
                                        }}
                                        getGlobalePrices={getGlobalePrices}
                                        getBOPISAvailability={getBOPISAvailability}
                                        setisBopisLoading={setisBopisLoading}
                                        clearBopisFlag={clearBopisFlag}
                                        basket={basket}
                                        oosBopisSkus={oosBopisSkus}
                                        hasBopisShipmentWithEngravingProduct={
                                            hasBopisShipmentWithEngravingProduct
                                        }
                                    />
                                </Stack>
                                <Stack
                                    spacing={4}
                                    backgroundColor="gray.100"
                                    padding="15px"
                                    {...cartStyles.mobileOrderSummaryContainer.inline}
                                    display={{base: 'flex', lg: 'none'}}
                                    marginBottom="-30px"
                                    paddingBottom="0px"
                                >
                                    <OrderSummary
                                        showPromoCodeForm={true}
                                        currencySymbol={currencySymbol}
                                        showPriceSkeleton={showPriceSkeleton}
                                        isEstimate={false}
                                        isOrderSummaryExpanded={isMobileOrderSummaryExpanded}
                                        onToggle={setIsMobileOrderSummaryExpanded}
                                        birthdayPromoCard={birthdayPromoCardNode}
                                    />
                                </Stack>

                                <Stack
                                    backgroundColor="gray.100"
                                    padding="15px"
                                    spacing="30px"
                                    margin={{base: '0px -15px 0px -15px', lg: '0px'}}
                                    gap="30px"
                                    className="conditions-cta-wrapper"
                                >
                                    <CartTermsAndConditions />
                                    <CartCta
                                        hasError={hasCartError}
                                        errorSources={errorSources}
                                        isLoading={isCartLoading}
                                        isCustomerEmployee={isCustomerEmployee}
                                        showExpressCheckout={true && countryISO === 'US'}
                                        selectedDelivery={selectedDelivery}
                                    />
                                </Stack>
                                <AcceptedPaymentMethods />
                                {renderPhilanthropyCallout({base: 'block', lg: 'none'})}
                                {enableWishlistInCart && countryISO === 'US' && (
                                    <Box
                                        width={{base: '100%', lg: 'calc(100% + 15px)'}}
                                        overflow="hidden"
                                        display={{base: 'block', lg: 'none'}}
                                    >
                                        <WishlistInCart />
                                    </Box>
                                )}
                                {cartRecommenderId && cartRecommenderId !== '' && (
                                    <Stack
                                        width="calc(100% + 15px)"
                                        overflow="hidden"
                                        className="cart-recommendations-wrapper"
                                        display={{base: 'block', lg: 'none'}}
                                    >
                                        <CartRecommendation
                                            cartRecommenderId={cartRecommenderId}
                                            openQuickAddModal={true}
                                        />
                                    </Stack>
                                )}
                            </GridItem>
                        </Grid>
                    </Stack>
                </Stack>
            </Container>
            <ConfirmationModal
                {...REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG}
                onPrimaryAction={() => {
                    handleRemoveItem(selectedItem)
                }}
                onAlternateAction={() => {}}
                {...modalProps}
            />

            {/* Bonus Product View Modal */}
            {bonusProductViewModal.isOpen && bonusProductViewModal.data && (
                <BonusProductViewModal
                    product={bonusProductViewModal.data.product}
                    isOpen={bonusProductViewModal.isOpen}
                    onClose={bonusProductViewModal.onClose}
                    bonusDiscountLineItemId={bonusProductViewModal.data.bonusDiscountLineItemId}
                    promotionId={bonusProductViewModal.data.promotionId}
                />
            )}
            {!isEGiftCardOnly && !isBopisShipment && !isCustomerLoggedIn && (
                <CheckoutProvider>
                    <BoltWrapper />
                </CheckoutProvider>
            )}
        </Box>
    )
}

Cart.getTemplateName = () => 'cart'

export default Cart
